/**
 * useOAuthFlow hook
 * Manages OAuth callback flow state and processing
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import { logger } from '../../helpers/logger.helper'
import { authLiveDebug } from '../helpers/authLiveDebug.helper'
import type { UseOAuthFlowReturn, OAuthErrorInfo } from '../types/signIn.types'

export function useOAuthFlow(): UseOAuthFlowReturn {
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const [isProcessing, setIsProcessing] = useState(true)
  const [errorInfo, setErrorInfo] = useState<OAuthErrorInfo | null>(null)

  // Determine loading message based on state
  const loadingMessage = isProcessing
    ? 'Verifying your account... this may take a moment if a security check was required.'
    : errorInfo
    ? errorInfo.userMessage
    : 'Loading...'

  const graceTimerStartRef = useRef<number | null>(null)

  useEffect(() => {
    authLiveDebug('OAuthFlow state', { isLoaded, isSignedIn, isProcessing })
  }, [isLoaded, isSignedIn, isProcessing])

  // Auto-detect when OAuth flow should be considered complete
  useEffect(() => {
    if (isLoaded) {
      // If user is signed in, OAuth flow is complete
      if (isSignedIn) {
        authLiveDebug('OAuthFlow isSignedIn — clearing isProcessing', {
          graceMs: graceTimerStartRef.current ? Date.now() - graceTimerStartRef.current : null,
        })
        logger.info('[OAuthFlow] isSignedIn — clearing isProcessing', {
          graceMs: graceTimerStartRef.current ? Date.now() - graceTimerStartRef.current : null,
        })
        setIsProcessing(false)
        setErrorInfo(null)
      } else {
        // If auth is loaded but user is not signed in, check for errors
        // This might indicate OAuth was cancelled or failed
        // Keep processing state longer to allow redirect to complete
        // Increased from 2s to 5s to handle slower computers and CSP delays
        graceTimerStartRef.current = Date.now()
        authLiveDebug('OAuthFlow grace window STARTED (5s until isProcessing=false)')
        logger.warn('[OAuthFlow] grace window started — will set isProcessing=false in 5s if still unsigned', {
          ts: graceTimerStartRef.current,
        })
        const timer = setTimeout(() => {
          authLiveDebug('OAuthFlow grace window EXPIRED — callback may bounce to sign-in', {
            ms: graceTimerStartRef.current ? Date.now() - graceTimerStartRef.current : 5000,
          })
          logger.warn('[OAuthFlow] grace window expired — isProcessing=false (callback may bounce to sign-in)', {
            ms: graceTimerStartRef.current ? Date.now() - graceTimerStartRef.current : 5000,
          })
          setIsProcessing(false)
        }, 5000)

        return () => clearTimeout(timer)
      }
    }
  }, [isLoaded, isSignedIn])

  // Handle setting processing state manually
  const handleSetIsProcessing = useCallback((value: boolean) => {
    setIsProcessing(value)
    if (value) {
      // Clear error when starting processing
      setErrorInfo(null)
    }
  }, [])

  return {
    isProcessing,
    setIsProcessing: handleSetIsProcessing,
    errorInfo,
    loadingMessage,
    isLoaded: isLoaded ?? false,
    isSignedIn,
  }
}
